// src/hooks/useBookings.ts
// ✅ QUICK FIX - Add missing useBookingDetails export

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bookingService } from '@/lib/api/bookingService';
import { 
  Booking,
  BookingFormData,
  BookingStatusUpdate,
  BookingFilters,
  TimelineEntry,
  CreateTimelineEntryData,
  UpdateTimelineEntryData,
  FollowUpEntry,
  CreateFollowUpData,
  UpdateFollowUpData,
  CompleteFollowUpData
} from '@/lib/api/bookingService';
import { 
  BookingQueryParams,
} from '@/types/booking';
import { tokenManager } from '@/lib/httpClient';

// Query keys for consistent cache management
export const bookingKeys = {
  all: ['bookings'] as const,
  lists: () => [...bookingKeys.all, 'list'] as const,
  list: (filters: BookingQueryParams) => [...bookingKeys.lists(), filters] as const,
  details: () => [...bookingKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...bookingKeys.details(), id] as const,
  stats: () => [...bookingKeys.all, 'stats'] as const,
  statsByPeriod: (period: string) => [...bookingKeys.stats(), period] as const,
  timeline: (bookingId: number | string) => [...bookingKeys.detail(bookingId), 'timeline'] as const,
  followUps: (bookingId: number | string) => [...bookingKeys.detail(bookingId), 'followups'] as const,
} as const;

// ✅ FIX: Individual booking hook (accepts both number and string)
export const useBooking = (bookingId: number | string) => {
  return useQuery({
    queryKey: bookingKeys.detail(bookingId),
    queryFn: async () => {
      const id = typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId;
      const response = await bookingService.getBookingById(id);
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch booking');
      }
      return response.data;
    },
    enabled: !!bookingId && !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error.message?.includes('404') || 
          error.response?.status === 404 ||
          error?.status === 401 || 
          error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// ✅ FIX: Add the missing useBookingDetails export (alias for useBooking)
export const useBookingDetails = (bookingId: number | string) => {
  return useBooking(bookingId);
};

// Bookings list hook with filters
export const useBookings = (params: BookingQueryParams = {}) => {
  return useQuery({
    queryKey: bookingKeys.list(params),
    queryFn: async () => {
      const response = await bookingService.getBookings(params);
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch bookings');
      }
      return response.data;
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Get booking statistics
export const useBookingStats = (period: string = 'monthly') => {
  return useQuery({
    queryKey: bookingKeys.statsByPeriod(period),
    queryFn: async () => {
      const response = await bookingService.getBookingStats(period);
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch booking statistics');
      }
      return response.data;
    },
    enabled: !!tokenManager.getToken(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Combined bookings with stats hook
export const useBookingsWithStats = (params: BookingQueryParams = {}) => {
  const bookingsQuery = useBookings(params);
  const statsQuery = useBookingStats();

  return {
    bookings: bookingsQuery.data?.bookings || [],
    pagination: bookingsQuery.data?.pagination || {
      current_page: 1,
      total_pages: 1,
      per_page: 10,
      total: 0,
      from: 0,
      to: 0,
      has_next: false,
      has_previous: false
    },
    stats: statsQuery.data || {
      total: 0,
      pending: 0,
      accepted: 0,
      completed: 0,
      rejected: 0,
      cancelled: 0
    },
    isLoading: bookingsQuery.isLoading || statsQuery.isLoading,
    hasError: bookingsQuery.error || statsQuery.error,
    refetchAll: () => {
      bookingsQuery.refetch();
      statsQuery.refetch();
    },
    error: bookingsQuery.error || statsQuery.error
  };
};


// Create new booking
export const useCreateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bookingData: BookingFormData) => {
      const response = await bookingService.createBooking(bookingData);
      if (!response.success) {
        throw new Error(response.message || 'Failed to create booking');
      }
      return response.data;
    },
    onSuccess: (newBooking) => {
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.stats() });
      
      if (newBooking?.booking) {
        queryClient.setQueryData(
          bookingKeys.detail(newBooking.booking.booking_id),
          newBooking.booking
        );
      }
    },
  });
};

// Update booking status
export const useUpdateBookingStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      statusUpdate 
    }: { 
      bookingId: number; 
      statusUpdate: BookingStatusUpdate; 
    }) => {
      const response = await bookingService.updateBookingStatus(bookingId, statusUpdate);
      if (!response.success) {
        throw new Error(response.message || 'Failed to update booking status');
      }
      return response.data;
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: bookingKeys.detail(variables.bookingId) });

      // Snapshot the previous value
      const previousBooking = queryClient.getQueryData(bookingKeys.detail(variables.bookingId));

      // Optimistically update to new value
      if (previousBooking) {
        queryClient.setQueryData(bookingKeys.detail(variables.bookingId), {
          ...previousBooking,
          status: variables.statusUpdate.status,
          updated_at: new Date().toISOString()
        });
      }

      // Return a context object with the snapshotted value
      return { previousBooking };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBooking) {
        queryClient.setQueryData(bookingKeys.detail(variables.bookingId), context.previousBooking);
      }
    },
    onSuccess: (updatedBooking, variables) => {
      // Update the specific booking in cache with fresh data from server
      if (updatedBooking?.booking) {
        queryClient.setQueryData(
          bookingKeys.detail(variables.bookingId),
          updatedBooking.booking
        );
      }
      
      // Invalidate lists and stats
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.stats() });
    },
    onSettled: (data, error, variables) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(variables.bookingId) });
    },
  });
};

// Cancel booking
export const useCancelBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      reason 
    }: { 
      bookingId: number; 
      reason?: string; 
    }) => {
      const response = await bookingService.cancelBooking(bookingId, reason);
      if (!response.success) {
        throw new Error(response.message || 'Failed to cancel booking');
      }
      return response.data;
    },
    onSuccess: (updatedBooking, variables) => {
      if (updatedBooking?.booking) {
        queryClient.setQueryData(
          bookingKeys.detail(variables.bookingId),
          updatedBooking.booking
        );
      }
      
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.stats() });
    },
  });
};

// Reschedule booking
export const useRescheduleBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      newDateTime, 
      reason 
    }: { 
      bookingId: number; 
      newDateTime: string; 
      reason?: string; 
    }) => {
      const response = await bookingService.rescheduleBooking(bookingId, newDateTime, reason);
      if (!response.success) {
        throw new Error(response.message || 'Failed to reschedule booking');
      }
      return response.data;
    },
    onSuccess: (updatedBooking, variables) => {
      if (updatedBooking?.booking) {
        queryClient.setQueryData(
          bookingKeys.detail(variables.bookingId),
          updatedBooking.booking
        );
      }
      
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
    },
  });
};

// Complete booking
export const useCompleteBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      completionNotes 
    }: { 
      bookingId: number; 
      completionNotes?: string; 
    }) => {
      const response = await bookingService.completeBooking(bookingId, completionNotes);
      if (!response.success) {
        throw new Error(response.message || 'Failed to complete booking');
      }
      return response.data;
    },
    onSuccess: (updatedBooking, variables) => {
      if (updatedBooking?.booking) {
        queryClient.setQueryData(
          bookingKeys.detail(variables.bookingId),
          updatedBooking.booking
        );
      }
      
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      queryClient.invalidateQueries({ queryKey: bookingKeys.stats() });
    },
  });
};

// Rate booking
export const useRateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      rating, 
      review 
    }: { 
      bookingId: number; 
      rating: number; 
      review?: string; 
    }) => {
      const response = await bookingService.rateBooking(bookingId, rating, review);
      if (!response.success) {
        throw new Error(response.message || 'Failed to rate booking');
      }
      return response.data;
    },
    onSuccess: (updatedBooking, variables) => {
      if (updatedBooking?.booking) {
        queryClient.setQueryData(
          bookingKeys.detail(variables.bookingId),
          updatedBooking.booking
        );
      }
      
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
    },
  });
};


// ===== TIMELINE HOOKS =====

// Get timeline entries for a booking
export const useTimeline = (bookingId: number | string, page: number = 1, limit: number = 20) => {
  return useQuery({
    queryKey: bookingKeys.timeline(bookingId),
    queryFn: async () => {
      const id = typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId;
      const response = await bookingService.getTimeline(id, page, limit);
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch timeline');
      }
      return response.data;
    },
    enabled: !!bookingId && !!tokenManager.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) {
        return false; // Don't retry for not found
      }
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Add timeline entry mutation
export const useAddTimelineEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, entry }: { bookingId: number; entry: CreateTimelineEntryData }) => {
      const response = await bookingService.addTimelineEntry(bookingId, entry);
      if (!response.success) {
        // Preserve detailed validation errors from API response
        const error = new Error(response.message || 'Failed to add timeline entry');
        (error as any).validationErrors = response.data?.errors;
        (error as any).apiResponse = response;
        throw error;
      }
      return response.data;
    },
    onSuccess: (newEntry, variables) => {
      // Update timeline cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.timeline(variables.bookingId) });
      
      // Update booking detail cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(variables.bookingId) });
    },
  });
};

// Update timeline entry mutation
export const useUpdateTimelineEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      timelineId, 
      entry 
    }: { 
      bookingId: number; 
      timelineId: number; 
      entry: UpdateTimelineEntryData; 
    }) => {
      const response = await bookingService.updateTimelineEntry(bookingId, timelineId, entry);
      if (!response.success) {
        // Preserve detailed validation errors from API response
        const error = new Error(response.message || 'Failed to update timeline entry');
        (error as any).validationErrors = response.data?.errors;
        (error as any).apiResponse = response;
        throw error;
      }
      return response.data;
    },
    onSuccess: (updatedEntry, variables) => {
      // Update timeline cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.timeline(variables.bookingId) });
    },
  });
};

// Delete timeline entry mutation
export const useDeleteTimelineEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      timelineId 
    }: { 
      bookingId: number; 
      timelineId: number; 
    }) => {
      const response = await bookingService.deleteTimelineEntry(bookingId, timelineId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete timeline entry');
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Update timeline cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.timeline(variables.bookingId) });
    },
  });
};

// ===== FOLLOW-UPS HOOKS =====

// Get follow-ups for a booking
export const useFollowUps = (bookingId: number | string) => {
  return useQuery({
    queryKey: bookingKeys.followUps(bookingId),
    queryFn: async () => {
      const id = typeof bookingId === 'string' ? parseInt(bookingId, 10) : bookingId;
      const response = await bookingService.getFollowUps(id);
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch follow-ups');
      }
      return response.data;
    },
    enabled: !!bookingId && !!tokenManager.getToken(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) {
        return false; // Don't retry for not found
      }
      if (error?.status === 401 || error?.status === 403) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Create follow-up mutation
export const useCreateFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, followUp }: { bookingId: number; followUp: CreateFollowUpData }) => {
      const response = await bookingService.createFollowUp(bookingId, followUp);
      if (!response.success) {
        throw new Error(response.message || 'Failed to create follow-up');
      }
      return response.data;
    },
    onSuccess: (newFollowUp, variables) => {
      // Update follow-ups cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.followUps(variables.bookingId) });
      
      // Update booking detail cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(variables.bookingId) });
    },
  });
};

// Update follow-up mutation
export const useUpdateFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      followUpId, 
      followUp 
    }: { 
      bookingId: number; 
      followUpId: number; 
      followUp: UpdateFollowUpData; 
    }) => {
      const response = await bookingService.updateFollowUp(bookingId, followUpId, followUp);
      if (!response.success) {
        throw new Error(response.message || 'Failed to update follow-up');
      }
      return response.data;
    },
    onSuccess: (updatedFollowUp, variables) => {
      // Update follow-ups cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.followUps(variables.bookingId) });
    },
  });
};

// Complete follow-up mutation
export const useCompleteFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      followUpId, 
      completionData 
    }: { 
      bookingId: number; 
      followUpId: number; 
      completionData: CompleteFollowUpData; 
    }) => {
      const response = await bookingService.completeFollowUp(bookingId, followUpId, completionData);
      if (!response.success) {
        throw new Error(response.message || 'Failed to complete follow-up');
      }
      return response.data;
    },
    onSuccess: (completedFollowUp, variables) => {
      // Update follow-ups cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.followUps(variables.bookingId) });
    },
  });
};

// Delete follow-up mutation
export const useDeleteFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      followUpId 
    }: { 
      bookingId: number; 
      followUpId: number; 
    }) => {
      const response = await bookingService.deleteFollowUp(bookingId, followUpId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete follow-up');
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      // Update follow-ups cache
      queryClient.invalidateQueries({ queryKey: bookingKeys.followUps(variables.bookingId) });
    },
  });
};

// Additional exports for backward compatibility
export {
  useBooking as useGetBooking,
  useBookings as useGetBookings,
  useBookingStats as useGetBookingStats,
};